import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

/**
 * Espelha `AuditEventResponseDto`.
 *
 * O evento e gravado por **ator**, nao por usuario: pode ser uma pessoa, um
 * serviço ou um job. Por isso `actorId`/`actorName`/`actorType`. Nao existem
 * `description` nem `metadata` — existe `diff`, que e o que mudou.
 */
export interface AuditLog {
  id: string;
  tenantId: string;
  actorType: string;
  actorId: string;
  actorName: string;
  action: string;
  resource: string;
  resourceId: string;
  result: string;
  entityType?: string | null;
  entityId?: string | null;
  version?: number | null;
  diff?: string | null;
  correlationId?: string | null;
  causationId?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  timestamp: string;
  createdAt: string;
}

/** Espelha `AuditLogResponseDto`: `pageSize`/`totalPages`, nao `limit`. */
export interface AuditLogPage {
  data: AuditLog[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AuditLogFilters {
  /** USER | SERVICE | JOB | SYSTEM — quem agiu. */
  actorType?: string;
  actorId?: string;
  action?: string;
  resource?: string;
  /** SUCCESS | FAILURE | DENIED. */
  result?: string;
  entityType?: string;
  correlationId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

export function useAuditLogs(params?: AuditLogFilters): ReturnType<typeof useQuery<AuditLogPage>> {
  return useQuery<AuditLogPage>({
    queryKey: queryKeys.audit.logs(params as Record<string, unknown>),
    queryFn: async () =>
      /* `/audit/log`, singular — a tela pedia `/audit/logs` e levava 404. */
      apiClient.get<AuditLogPage>('/audit/log', {
        params: {
          ...(params?.actorType ? { actorType: params.actorType } : {}),
          ...(params?.actorId ? { actorId: params.actorId } : {}),
          ...(params?.action ? { action: params.action } : {}),
          ...(params?.resource ? { resource: params.resource } : {}),
          ...(params?.result ? { result: params.result } : {}),
          ...(params?.entityType ? { entityType: params.entityType } : {}),
          ...(params?.correlationId ? { correlationId: params.correlationId } : {}),
          ...(params?.dateFrom ? { dateFrom: params.dateFrom } : {}),
          ...(params?.dateTo ? { dateTo: params.dateTo } : {}),
          page: String(params?.page ?? 1),
          pageSize: String(params?.pageSize ?? 20),
        },
      }),
  });
}

/** `GET /audit/stats` — contagem por acao e por ator, calculada no servidor. */
export interface AuditStats {
  byAction: { action: string; count: number }[];
  byActor: { actorId: string; count: number }[];
}

export function useAuditStats(params?: { dateFrom?: string; dateTo?: string }) {
  return useQuery<AuditStats>({
    queryKey: ['audit', 'stats', params?.dateFrom, params?.dateTo],
    queryFn: async () =>
      apiClient.get<AuditStats>('/audit/stats', {
        params: {
          ...(params?.dateFrom ? { dateFrom: params.dateFrom } : {}),
          ...(params?.dateTo ? { dateTo: params.dateTo } : {}),
        },
      }),
  });
}

export function useAuditLogDetail(id: string): ReturnType<typeof useQuery<AuditLog>> {
  return useQuery<AuditLog>({
    queryKey: queryKeys.audit.logDetail(id),
    queryFn: async () => apiClient.get<AuditLog>(`/audit/log/${id}`),
    enabled: !!id,
  });
}